import axios from "axios";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { SignOut } from "@utils/signout";

dayjs.extend(utc);
dayjs.extend(timezone);

const axiosBlacklistBearer = [
  `${process.env.REACT_APP_API_DOMAIN}/authentications/accesstoken`,
];

// Dedicated instance for calls to our own API. Only this instance gets the
// auth interceptor, so external requests (e.g. presigned S3 URLs) never risk
// picking up an Authorization header they didn't ask for.
const apiClient = axios.create();

apiClient.interceptors.request.use(
  (config: any) => {
    const url = config.url ?? "";
    const jwt_token = Cookies?.get("jwt_token_control") ?? "";
    if (axiosBlacklistBearer.some((v) => !url.includes(v))) {
      const token = jwt_token ?? "";
      config.headers.Authorization = token;
      config.headers["X-Time-Zone"] = dayjs.tz.guess();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Reactive token refresh -------------------------------------------------
// When the access token expires the API returns 401. Instead of pre-emptively
// refreshing on every navigation, we refresh once here, lazily, and retry the
// failed request. Concurrent 401s are queued so only ONE refresh call fires;
// the rest wait and retry with the new token. If refresh fails, we sign out.
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach((p) => {
    if (error || !token) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
};

// Statuses that mean the server actually JUDGED the refresh token and said
// no. Anything else — a network drop, a 5xx, the proxy's 60s timeout — is not
// a verdict on the session, and must not cost the user their login.
const isDefinitiveRejection = (err: any): boolean =>
  [400, 401, 403].includes(err?.response?.status);

const refreshAccessToken = async (): Promise<string> => {
  const refresh_token = Cookies?.get("jwt_refresh_token_control") ?? "";
  if (!refresh_token) throw new Error("no refresh token");
  let res: any;
  try {
    // Plain axios (no interceptor) so a 401 on the refresh call can't recurse.
    res = await axios.post(
      `${process.env.REACT_APP_API_DOMAIN}/authentications/refreshtoken`,
      {
        refresh_token,
        client_id: process.env.REACT_APP_CLIENT_ID,
        client_secret: process.env.REACT_APP_CLIENT_SECRET,
      }
    );
  } catch (err: any) {
    // Two tabs can 401 in the same instant: the isRefreshing gate is
    // per-tab, cookies are per-browser. The losing tab sends a refresh token
    // the winner already consumed and gets rejected — but the winner has
    // ALREADY written fresh cookies by then. So on a definitive rejection,
    // re-read: if the cookie no longer matches what we sent, the race was
    // lost, not the session — use the winner's tokens instead of signing
    // the user out.
    if (isDefinitiveRejection(err)) {
      const currentRefresh = Cookies?.get("jwt_refresh_token_control") ?? "";
      const currentToken = Cookies?.get("jwt_token_control") ?? "";
      if (currentRefresh && currentRefresh !== refresh_token && currentToken) {
        return currentToken;
      }
    }
    throw err;
  }
  const token = res?.data?.data?.token ?? "";
  const newRefreshToken = res?.data?.data?.refresh_token ?? "";
  if (!token) throw new Error("refresh failed");
  Cookies.set("jwt_token_control", token, {
    expires: parseInt(process.env?.REACT_APP_JWT_EXP ?? "7"),
  });
  // Rotate ONLY when the server actually sent a replacement. This used to
  // write the field unconditionally, so a refresh response without a
  // refresh_token overwrote a working cookie with "" — everything then kept
  // working for ~7 more days on the new access token, and the NEXT expiry hit
  // "no refresh token" and bounced a daily user to the login page with no
  // visible cause.
  if (newRefreshToken) {
    Cookies.set("jwt_refresh_token_control", newRefreshToken, {
      expires: parseInt(process.env?.REACT_APP_JWT_REFRESH_EXP ?? "14"),
    });
  }
  return token;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const originalRequest = error.config ?? {};
    const status = error.response?.status;
    const url = originalRequest.url ?? "";
    const isAuthEndpoint =
      url.includes("/authentications/refreshtoken") ||
      url.includes("/authentications/accesstoken");

    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // A refresh is already in flight — wait for it, then retry.
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest._retry = true;
        originalRequest.headers.Authorization = token;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;
    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = newToken;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Only a real verdict ends the session. On a transient failure the
      // user keeps their cookies; the next 401 simply tries again.
      if (
        isDefinitiveRejection(refreshError) ||
        (refreshError as any)?.message === "no refresh token"
      ) {
        SignOut();
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const apiGet = async (url: string, headers: any, language: string) => {
  try {
    const res = await apiClient.get(url, {
      headers: { "Accept-Language": language, ...headers },
    });
    return {
      status: true,
      code: res?.status ?? 200,
      data: res?.data ?? {},
    };
  } catch (error: any) {
    console.error("error", error);
    return {
      status: false,
      code: error?.response?.status ?? 500,
      data: error?.response?.data ?? {},
    };
  }
};

export const apiPost = async (
  url: string,
  params: any,
  headers: any,
  language: string
) => {
  try {
    const res = await apiClient.post(url, params, {
      headers: { "Accept-Language": language, ...headers },
    });
    return {
      status: true,
      code: res?.status ?? 200,
      data: res?.data ?? {},
    };
  } catch (error: any) {
    console.error("error", error);
    return {
      status: false,
      code: error?.response?.status ?? 500,
      data: error?.response?.data ?? {},
      error: error,
    };
  }
};

export const apiPut = async (
  url: string,
  params: any,
  headers: any,
  language: string
) => {
  try {
    const res = await apiClient.put(url, params, {
      headers: { "Accept-Language": language, ...headers },
    });
    return {
      status: true,
      code: res?.status ?? 200,
      data: res?.data ?? {},
    };
  } catch (error: any) {
    console.error("error", error);
    return {
      status: false,
      code: error?.response?.status ?? 500,
      data: error?.response?.data ?? {},
    };
  }
};

export const apiDelete = async (url: string, language: string) => {
  try {
    const res = await apiClient.delete(url, {
      headers: {
        "Accept-Language": language,
      },
    });
    return {
      status: true,
      code: res?.status ?? 200,
      data: res?.data ?? {},
    };
  } catch (error: any) {
    console.error("error", error);
    return {
      status: false,
      code: error?.response?.status ?? 500,
      data: error?.response?.data ?? {},
    };
  }
};

export const apiGetS3 = async (url: string, headers: any) => {
  try {
    const res = await axios.get(url, {
      headers: { ...headers },
    });
    return {
      status: true,
      code: res?.status ?? 200,
      data: res?.data ?? {},
    };
  } catch (error: any) {
    console.error("error", error);
    return {
      status: false,
      code: error?.response?.status ?? 500,
      data: error?.response?.data ?? {},
    };
  }
};
